import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Home, Wrench, Shield, Search, MapPin, Key, ClipboardList, Phone,
  FileText, Vote, Calculator, PenTool, CheckSquare, ShoppingCart, UserCircle, Users,
  AlertCircle, RefreshCw
} from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import { getRoleLabel } from './config/roleLabels';
import type { Lens } from './layouts/AppShell';
import { PageHeader } from './components/ui/PageHeader';
import { Card } from './components/ui/Card';
import { StatTile } from './components/ui/StatTile';
import { financialAPI, securityAPI, announcementsAPI } from './services/api';
import { homeApi } from './services/homeApi';
import { useActiveHomeUnitId } from './hooks/useActiveHomeUnitId';
import { domainForModule, usePermissions } from './hooks/usePermissions';
import { CondoOnboarding } from './components/CondoLinking';
import { homeModuleEntitled } from './utils/homeEntitlementGate';
import billingApi from './services/billingApi';
import { isPlanVisible } from './config/navigation';
// P2-B (2026-09-08): Cumpleaños de este mes widget (condo lens).
import BirthdaysWidget from './components/BirthdaysWidget';

interface Dashboard2Props {
  forceLens?: Lens;
}

interface DashboardStats {
  cuotaMantenimiento: string;
  visitasHoy: string;
  avisosNuevos: string;
  tasaMorosidad: string;
}

const EMPTY_STATS: DashboardStats = {
  cuotaMantenimiento: '—',
  visitasHoy: '—',
  avisosNuevos: '—',
  tasaMorosidad: '—',
};

export default function Dashboard2({ forceLens = 'home' }: Dashboard2Props) {
  const { user, roles, activeCondoId, activeOwnedUnitId, ownedUnits, loading: authLoading } = useAuth();
  const { can, loading: permissionsLoading } = usePermissions();
  const navigate = useNavigate();
  // FEATURE (2026-08-31, Jaime): "Compras" box on the home Resumen dashboard.
  // Count = distinct pending line items on the ACTIVE unit's shopping list
  // ('Cebolla x 4' counts as 1). Fetched with the shared active-unit hook,
  // same unit-scoped pattern as HomeShopping. Employees are excluded: their
  // home dashboard section shows no Compras widget and the fetch is skipped
  // (employee scope for shopping itself is left unchanged).
  const activeHomeUnitId = useActiveHomeUnitId();
  const [shoppingPending, setShoppingPending] = useState<number>(0);
  // FEATURE (2026-09-22, Jaime): matching "Tareas" counter — pending tasks on
  // the ACTIVE home unit, same widget/count-badge behavior as Compras.
  const [tasksPending, setTasksPending] = useState<number>(0);
  const [entitlement, setEntitlement] = useState<{ plan?: string; tier?: string; modules?: string[]; features?: string[] } | null>(null);
  const [entitlementReady, setEntitlementReady] = useState(false);

  // Re-fetch when the active condo changes. With no condoId, the endpoint returns
  // the personal/home entitlement, which is the correct source for standalone homes.
  useEffect(() => {
    setEntitlement(null);
    setEntitlementReady(false);
    billingApi.getEntitlements(activeCondoId, activeOwnedUnitId)
      .then((e) => { setEntitlement(e); setEntitlementReady(true); })
      .catch(() => { setEntitlement(null); setEntitlementReady(true); });
  }, [activeCondoId, activeOwnedUnitId]);

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const isDirective = user?.role === 'directive' || user?.role === 'treasurer' || isAdmin;
  const isSecurity = user?.role === 'security';
  // FIX 2026-09-07 (Jaime parity): backend role is 'empleado' (the old
  // 'employee' comparison was dead), and the role input is context-aware:
  // the ACTIVE condo membership role wins; global user.role is only the
  // fallback when no context membership exists.
  const activeMembershipRole = activeCondoId ? roles.find((r) => r.condoId === activeCondoId)?.role : undefined;
  // FIX (Jaime bug 2026-09-09 PM, Francisco Caripa): the role input is now
  // LENS-aware. In the Hogar lens the active condo membership (and the global
  // 'empleado' fallback) belong to a DIFFERENT context — the user's own home
  // shows his role THERE. A standalone owned unit (no condo_id) is always his
  // Propietario role; an employee-housing unit (assigned) keeps 'Empleado'.
  // Condominio lens keeps the previous context-aware resolution.
  const activeHomeUnit = ownedUnits.find((unit) => unit.id === activeOwnedUnitId);
  const homeLensRole = activeHomeUnit
    ? (activeHomeUnit.assigned
        ? 'empleado'
        : (activeHomeUnit.condo_id
            ? (roles.find((r) => r.condoId === activeHomeUnit.condo_id)?.role || 'propietario')
            : 'propietario'))
    : activeMembershipRole || user?.role;
  const effectiveRole = forceLens === 'home' ? homeLensRole : (activeMembershipRole || user?.role);
  const isEmployee = effectiveRole === 'empleado';
  // Roles that may see financial summaries and visitor stats without getting 403
  const canSeeFinancial = isSuperAdmin || user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'treasurer' || user?.role === 'propietario';
  const canSeeVisitorStats = canSeeFinancial || isSecurity;

  const [stats, setStats] = useState<DashboardStats>(EMPTY_STATS);
  // D3 fix (2026-10-01): true when the visitors endpoint answered 403/401 —
  // the "Visitas Hoy" tile is hidden instead of showing a fabricated 0.
  const [visitorsForbidden, setVisitorsForbidden] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(false);
  // First successful load: after this, background refreshes are silent —
  // existing stat values stay on screen (no '...' flicker) while refetching.
  const [statsInitialized, setStatsInitialized] = useState(false);

  const getLensTitle = () => {
    switch (forceLens) {
      case 'building': return roles.find((r) => r.condoId === activeCondoId)?.condoName || (user as any)?.condoName || 'Mi Edificio';
      case 'home': return ownedUnits.find((unit) => unit.id === activeOwnedUnitId)?.household_name || ownedUnits.find((unit) => unit.id === activeOwnedUnitId)?.unit_number || user?.unitLabel || user?.unit || roles.find((r) => r.condoId === activeCondoId)?.unitLabel || roles.find((r) => r.condoId === activeCondoId)?.unitNumber || 'Mi Hogar';
      case 'services': return 'Negocios y Servicios';
      default: return 'Dashboard';
    }
  };

  const loadStats = useCallback(async () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const activeOwnedUnit = activeOwnedUnitId
      ? ownedUnits.find((unit) => unit.id === activeOwnedUnitId)
      : undefined;
    // An owned unit is the active context when selected. Its condo linkage is
    // authoritative; never fall back to the user's primary membership condo
    // for a standalone unit (or while that unit is still being hydrated).
    const condoId = activeOwnedUnitId
      ? activeOwnedUnit?.condo_id || undefined
      : activeCondoId || userData.condoId;
    if (!condoId) {
      setStats(EMPTY_STATS);
      return;
    }

    if (!statsInitialized) setStatsLoading(true);
    setStatsError(false);
    setVisitorsForbidden(false);

    try {
      const promises: Promise<any>[] = [];

      if (forceLens === 'home') {
        // Cuota mantenimiento → financial summary (admin/directivo/propietario only)
        if (canSeeFinancial && can('finance')) {
          promises.push(
            financialAPI.getSummary(condoId).catch(() => null)
          );
        } else {
          promises.push(Promise.resolve(null));
        }
        // Visitas hoy → security visitors (admin/directivo/propietario/security only)
        if (canSeeVisitorStats && can('visitor')) {
          // D3 fix (2026-10-01): a 403 on /security/condo/:id/visitors (the
          // endpoint is admin/security-scoped) must NOT collapse to null and
          // render a fake "Visitas Hoy 0". Surface the forbidden marker so the
          // widget hides itself instead of showing a fabricated count.
          promises.push(
            securityAPI.getVisitors(condoId).catch((e: any) => ({ visitorsForbidden: e?.response?.status === 403 || e?.response?.status === 401 }))
          );
        } else {
          promises.push(Promise.resolve(null));
        }
      }

      if (forceLens === 'building') {
        // Avisos nuevos → announcements
        if (can('announcement')) promises.push(
          announcementsAPI.getAll(condoId).catch(() => null)
        );
        if ((isAdmin || isDirective) && can('moroso')) {
          // Morosidad → financial morosos
          promises.push(
            financialAPI.getMorosos(condoId).catch(() => null)
          );
        }
      }

      const results = await Promise.all(promises);

      if (forceLens === 'home') {
        const [financialRes, visitorsRes] = results;
        const summary = financialRes?.data?.data || financialRes?.data || financialRes;
        // D3 fix (2026-10-01): 403 marker → widget hidden, never a fake 0.
        setVisitorsForbidden(!!(visitorsRes as any)?.visitorsForbidden);
        const visitors = (visitorsRes as any)?.visitorsForbidden ? [] : (visitorsRes?.data?.data || visitorsRes?.data || []);

        // Calculate today's visitors
        const today = new Date().toISOString().slice(0, 10);
        const todayVisitors = Array.isArray(visitors)
          ? visitors.filter((v: any) => {
              const visitDate = v.check_in || v.created_at || v.date || '';
              return visitDate.toString().slice(0, 10) === today;
            }).length
          : 0;

        setStats({
          cuotaMantenimiento: summary?.monthlyFee != null
            ? `$${Number(summary.monthlyFee).toLocaleString()}`
            : summary?.totalCharges != null
              ? `$${Number(summary.totalCharges).toLocaleString()}`
              : '—',
          visitasHoy: String(todayVisitors),
          avisosNuevos: '—',
          tasaMorosidad: '—',
        });
      }

      if (forceLens === 'building') {
        const announcementsRes = results[0];
        const announcements = announcementsRes?.data?.data || announcementsRes?.data || [];
        const unreadCount = Array.isArray(announcements)
          ? announcements.filter((a: any) => !a.read).length
          : 0;

        let morosidad = '—';
        if ((isAdmin || isDirective) && can('moroso')) {
          const morososRes = results[1];
          const morosos = morososRes?.data?.data || morososRes?.data || [];
          if (Array.isArray(morosos) && morosos.length > 0) {
            morosidad = `${morosos.length}`;
          } else if (Array.isArray(morosos) && morosos.length === 0) {
            morosidad = '0';
          }
        }

        setStats({
          cuotaMantenimiento: '—',
          visitasHoy: '—',
          avisosNuevos: String(unreadCount),
          tasaMorosidad: morosidad,
        });
      }
      setStatsInitialized(true);
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      // Keep the last good stats on error; only show the retry affordance.
      if (!statsInitialized) setStats(EMPTY_STATS);
      setStatsError(true);
    } finally {
      setStatsLoading(false);
    }
  }, [forceLens, isAdmin, isDirective, canSeeFinancial, canSeeVisitorStats, activeCondoId, activeOwnedUnitId, ownedUnits, can, statsInitialized]);

  useEffect(() => {
    // A context switch changes the data source; re-allow a visible first load.
    setStatsInitialized(false);
  }, [forceLens, activeCondoId, activeOwnedUnitId]);
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleRetry = () => loadStats();

  // FEATURE (2026-08-31, Jaime): pending shopping-item count for the Compras
  // box. Distinct line items only — quantity is irrelevant to the count.
  // Fetch is skipped for non-home lenses and for employees; 0 is a valid,
  // gracefully-shown state (backend errors also render as 0).
  useEffect(() => {
    if (forceLens !== 'home' || isEmployee || !activeHomeUnitId) {
      setShoppingPending(0);
      return;
    }
    // fix-173c: usuariosOnly / home-* stripped payload → no /api/home/shopping call.
    if (!entitlementReady) { setShoppingPending(0); return; }
    if (!homeModuleEntitled(entitlement, 'home-shopping')) { setShoppingPending(0); return; }
    let cancelled = false;
    homeApi.shopping.list(activeHomeUnitId)
      .then((res: any) => {
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        const rawItems = payload?.items || payload?.data?.items || (Array.isArray(payload) ? payload : []);
        const pending = Array.isArray(rawItems)
          ? rawItems.filter((it: any) => {
              const status = (it?.status ?? '').toString().toLowerCase();
              return !(status === 'completed' || it?.purchased === true || !!it?.purchased_at);
            }).length
          : 0;
        setShoppingPending(pending);
      })
      .catch(() => { if (!cancelled) setShoppingPending(0); });
    return () => { cancelled = true; };
  }, [forceLens, isEmployee, activeHomeUnitId, entitlement, entitlementReady]);

  // FEATURE (2026-09-22, Jaime): pending-task count for the Tareas box —
  // mirrors the Compras data-source pattern (shared active-unit hook +
  // homeApi list; employees excluded, 0 on error/empty). "Pending" matches
  // HomeTasks' own definition: status !== 'completed' and not archived
  // (completed tasks auto-archive server-side 24h after completion).
  useEffect(() => {
    if (forceLens !== 'home' || isEmployee || !activeHomeUnitId) {
      setTasksPending(0);
      return;
    }
    // fix-173c: usuariosOnly / home-* stripped payload → no /api/home/tasks call.
    if (!entitlementReady) { setTasksPending(0); return; }
    if (!homeModuleEntitled(entitlement, 'home-tasks')) { setTasksPending(0); return; }
    let cancelled = false;
    homeApi.tasks.list(activeHomeUnitId)
      .then((res: any) => {
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        const rawTasks = payload?.tasks || payload?.data?.tasks || (Array.isArray(payload) ? payload : []);
        const pending = Array.isArray(rawTasks)
          ? rawTasks.filter((t: any) => {
              const status = (t?.status ?? 'pending').toString().toLowerCase();
              const archived = !!(t?.archived_at ?? t?.archivedAt);
              return status !== 'completed' && !archived;
            }).length
          : 0;
        setTasksPending(pending);
      })
      .catch(() => { if (!cancelled) setTasksPending(0); });
    return () => { cancelled = true; };
  }, [forceLens, isEmployee, activeHomeUnitId, entitlement, entitlementReady]);

  // Do not render either dashboard variant until auth/permissions have settled. Once
  // settled, an empty membership or missing active condo is the canonical unlinked state.
  const isUnlinked = !authLoading && !permissionsLoading && (roles.length === 0 || !activeCondoId);
  // F-2 (2026-09-23, standalone → condo-admin flow): CTA on the standalone
  // home dashboard — visible only while the user has NO condo link (no condo
  // membership with a condoId, and no user.condoId) and owns an unlinked
  // unit that is not an employee assignment. Points to the /create-condo
  // wizard; disappears once a condo link exists.
  const standaloneOwnedUnit = (ownedUnits || []).find((u) => !u.condo_id && !u.assigned);
  const hasNoCondoLink = !user?.condoId && !roles.some((r) => r.condoId);
  const showCreateCondoCta = forceLens === 'home' && !isEmployee && !!standaloneOwnedUnit && hasNoCondoLink;
  if (authLoading || permissionsLoading) {
    return <div className="h-full min-h-[60vh] animate-pulse bg-[var(--v-base-bg)]" aria-label="Cargando dashboard" />;
  }

  return (
    <div className="h-full overflow-auto bg-[var(--v-base-bg)]">
      <div className="max-w-7xl mx-auto">
        <PageHeader 
          title={getLensTitle()} 
          subtitle={`Bienvenido, ${user?.name || 'Usuario'}. Rol: ${getRoleLabel(effectiveRole || 'residente')}`}
        />
        {/* Stat row — real data from API, empty states when no data */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {forceLens === 'building' && can('announcement') && (
            <>
              <StatTile 
                label="Avisos Nuevos" 
                value={statsLoading ? '...' : stats.avisosNuevos} 
                icon={<FileText size={20}/>} 
              />
              {(isAdmin || isDirective) && can('moroso') && (
                <StatTile 
                  label="Morosos" 
                  value={statsLoading ? '...' : stats.tasaMorosidad}
                  icon={<Shield size={20}/>}
                />
              )}
              {statsError && (
                <button 
                  onClick={handleRetry}
                  className="col-span-2 flex items-center justify-center gap-2 text-sm text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]"
                >
                  <RefreshCw size={16} /> Reintentar
                </button>
              )}
            </>
          )}
          {forceLens === 'home' && (
            <>
              {can('finance') && <StatTile 
                label="Cuota Mantenimiento" 
                value={statsLoading ? '...' : stats.cuotaMantenimiento}
                icon={<Calculator size={20}/>} 
              />}
              {can('visitor') && !visitorsForbidden && <StatTile 
                label="Visitas Hoy" 
                value={statsLoading ? '...' : stats.visitasHoy} 
                icon={<Users size={20}/>} 
              />}
              {statsError && (
                <button 
                  onClick={handleRetry}
                  className="col-span-2 flex items-center justify-center gap-2 text-sm text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]"
                >
                  <RefreshCw size={16} /> Reintentar
                </button>
              )}
            </>
          )}
        </div>

        {/* F-2: standalone → condo-admin CTA (only while no condo link exists). */}
        {showCreateCondoCta && (
          <Card onClick={() => navigate('/create-condo')} className="p-6 mb-8 flex flex-col sm:flex-row sm:items-center gap-4 border-l-4 border-l-[var(--v-teal-400)] hover:opacity-95 transition-opacity cursor-pointer">
            <div className="p-3 rounded-[var(--v-radius-lg)] bg-[var(--v-teal-50)] text-[var(--v-teal-700)]"><Building2 className="w-6 h-6" /></div>
            <div className="flex-1">
              <p className="font-bold text-lg text-[var(--v-text-primary)] leading-tight">Crear condominio</p>
              <p className="text-sm text-[var(--v-text-secondary)] mt-1">Convierte tu casa en la dirección de un condominio y administra su comunidad. Tu unidad quedará vinculada y serás el administrador. Inicia en el plan Free.</p>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {/* Render widgets structurally based on lens + role */}
          
          {forceLens === 'building' && isSecurity && (
            <>
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={Search} label="Verificar QR" domain="access" planModule="qr" onClick={() => navigate('/building/verify')} />
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={MapPin} label="Registro Visitas" domain="visitors" planModule="reporte-visitas" onClick={() => navigate('/building/visitors')} />
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={Key} label="Llave Digital" domain="access" planModule="smart-access" onClick={() => navigate('/building/smart-access')} />
            </>
          )}

          {forceLens === 'building' && !isSecurity && (
            <>
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={Building2} label="Muro Comunidad" domain="dashboard" planModule="dashboard" onClick={() => navigate('/building/wall')} />
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={FileText} label="Anuncios" domain="announcements" planModule="announcements" onClick={() => navigate('/building/announcements')} />
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={Vote} label="Votaciones" domain="voting" planModule="voting" onClick={() => navigate('/building/voting')} />
              
              {(isAdmin || isDirective) && (
                <>
                   <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={Calculator} label="Gastos" domain="expenses" planModule="expenses" onClick={() => navigate('/building/expenses')} admin={true} />
                   <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={Shield} label="Morosos" domain="morosos" planModule="morosos" onClick={() => navigate('/building/morosos')} admin={true} />
                   <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={Users} label="Residentes" domain="residents" planModule="user-management" onClick={() => navigate('/user-management')} admin={true} />
                </>
              )}
            </>
          )}

          {forceLens === 'home' && isEmployee && (
            <>
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={CheckSquare} label="Mis Tareas" domain="home" planModule="tasks" onClick={() => navigate('/home/tasks')} />
              {/* (Jaime 2026-09-07) 'Mis Pagos' widget removed: payroll eliminated from employee view for now; page + route stay intact. */}
            </>
          )}

          {forceLens === 'home' && !isEmployee && (
            <>
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={Home} label="Resumen Hogar" domain="home" planModule="dashboard" onClick={() => navigate('/home/dashboard')} />
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={ShoppingCart} label="Compras" domain="home" planModule="shopping" count={shoppingPending} onClick={() => navigate('/home/shopping')} />
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={CheckSquare} label="Tareas Familiares" domain="home" planModule="tasks" count={tasksPending} onClick={() => navigate('/home/tasks')} />
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={UserCircle} label="Personal Doméstico" domain="user" planModule="user-management" onClick={() => navigate('/home/staff-directory')} admin={true} />
              <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={Building2} label="Rentas" domain="home" planModule="reservations" onClick={() => navigate('/home/reservations')} admin={true} />
            </>
          )}

          {/* P2-B (2026-09-08): Cumpleaños de este mes — full-width row under the widget grid. Hides itself when empty. */}
          {forceLens === 'building' && <BirthdaysWidget />}

          {forceLens === 'services' && (
            <>
               <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={Wrench} label="Explorar Directorio" domain="supplier" planModule="supplier-directory" onClick={() => navigate('/services/suppliers')} />
               <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={ShoppingCart} label="Mis Proveedores" domain="supplier" planModule="supplier-directory" onClick={() => navigate('/services/suppliers')} />
               <Widget entitlement={entitlement} entitlementReady={entitlementReady} canPerm={can} permissionsLoading={permissionsLoading} icon={CheckSquare} label="Solicitar Servicio" domain="maintenance" planModule="work-orders" onClick={() => navigate('/services/work-orders')} />
            </>
          )}
          
        </div>
      </div>
    </div>
  );
}

function Widget({ icon: Icon, label, onClick, admin = false, domain, planModule, entitlement, entitlementReady, canPerm, permissionsLoading, count }: { icon: any, label: string, onClick: () => void, admin?: boolean, domain?: string, planModule?: string, entitlement?: { plan?: string; tier?: string; modules?: string[]; features?: string[] } | null, entitlementReady: boolean, canPerm: (d: string) => boolean, permissionsLoading: boolean, count?: number }) {
  if (domain && !permissionsLoading && !canPerm(domainForModule(domain))) return null;
  // Plan-gate only once entitlements have loaded — never hide buttons while the fetch is in flight.
  if (planModule && entitlementReady && !isPlanVisible({ key: planModule, label, icon: Icon, routes: { home: '', condo: '' }, planModule }, entitlement || null, true)) return null;
  return (
    <Card onClick={onClick} className={`p-6 flex flex-col items-start gap-4 ${admin ? 'border-l-4 border-l-[var(--v-warning)]' : 'border-l-4 border-l-[var(--v-teal-400)]'}`}>
      <div className="flex items-center justify-between w-full">
        <div className={`p-3 rounded-[var(--v-radius-lg)] ${admin ? 'bg-[var(--v-warning-bg)] text-[var(--v-warning)]' : 'bg-[var(--v-teal-50)] text-[var(--v-teal-700)]'}`}>
          <Icon className="w-6 h-6" />
        </div>
        {/* FEATURE (2026-08-31, Jaime): optional count badge (Compras). 0 is
            shown gracefully — same badge style, value "0". */}
        {typeof count === 'number' && (
          <span className="min-w-[2rem] px-2 py-1 rounded-full text-sm font-bold text-center bg-[var(--v-teal-50)] text-[var(--v-teal-700)] border border-[var(--v-teal-400)]">
            {count}
          </span>
        )}
      </div>
      <div>
        <p className="font-bold text-lg text-[var(--v-text-primary)] leading-tight">{label}</p>
        <p className="text-sm text-[var(--v-text-secondary)] mt-1">{admin ? 'Gestión' : 'Acceso Rápido'}</p>
      </div>
    </Card>
  );
}
